import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, ArrowRight, Clock, BookOpen, FileText, ClipboardList } from 'lucide-react';
import api from '../lib/api';
import { useCountdown } from './CountdownText';

/** "You have CA$100 in your account" — free students only; hidden when the balance is 0. Live countdown under 48 h. */
export function CreditsCard({ compact }) {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  useEffect(() => { api.get('/api/credits/me').then((r) => setC(r.data)).catch(() => setC(null)); }, []);
  const cd = useCountdown(c?.active ? c.expires_at : null);
  if (!c || !c.active || cd?.expired) return null;
  const urgent = !!cd?.urgent;
  return (
    <div className={`rounded-3xl border ${urgent ? 'border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50' : 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50'} shadow-[0_2px_12px_rgba(0,0,0,0.05)] ${compact ? 'p-4' : 'p-4 sm:p-5 md:p-6'}`} data-testid="credits-card">
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl ${urgent ? 'bg-rose-500' : 'bg-emerald-600'} text-white flex items-center justify-center shrink-0`}><Wallet size={20} /></div>
        <div className="flex-1 min-w-[160px]">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Your wallet credits</p>
          <p className="font-[Manrope] text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight" data-testid="credits-balance">{c.balance}</p>
          <p className={`text-xs mt-1 inline-flex items-center gap-1 flex-wrap ${urgent ? 'text-rose-700 font-semibold' : 'text-slate-600'}`} data-testid="credits-expiry">
            <Clock size={11} /> <span data-testid="credits-countdown">{urgent ? `Expires ${cd.text}` : `Expires ${c.expires_text}`}</span>{urgent && c.expires_at_text ? <span className="font-semibold" data-testid="credits-expires-at">(at {c.expires_at_text})</span> : null}
            <span className="font-normal text-slate-500 hidden sm:inline">· only for the Pro pass{c.min_order ? ` (orders of ${c.min_order}+)` : ''} · code <span className="font-mono font-bold">{c.code}</span></span>
            <span className="font-normal text-slate-500 sm:hidden">· code <span className="font-mono font-bold">{c.code}</span></span>
          </p>
        </div>
        <button type="button" onClick={() => navigate(`/checkout?code=${encodeURIComponent(c.code)}`)} className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 ${urgent ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'} text-white text-sm font-bold rounded-full px-5 py-2.5 transition shadow-lg`} data-testid="credits-use-btn">
          Use my credits — pay {c.pay} <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

const ICON = { course: BookOpen, exam: FileText, plan: ClipboardList };

/** Everything a free student was granted on a live class (courses / mock tests / study plan). */
export function UnlocksCard() {
  const navigate = useNavigate();
  const [u, setU] = useState(null);
  useEffect(() => { api.get('/api/me/unlocks').then((r) => setU(r.data)).catch(() => setU(null)); }, []);
  if (!u) return null;
  const items = [
    ...(u.study_plan ? [{ kind: 'plan', label: 'Your personal study plan', to: '/study-plan' }] : []),
    ...u.courses.map((c) => ({ kind: 'course', label: c.title, to: `/courses/${c.slug}` })),
    ...u.exams.map((e) => ({ kind: 'exam', label: e.name, to: `/exams/${e.id}` })),
  ];
  if (!items.length) return null;
  return (
    <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-5 md:p-6 shadow-[0_2px_12px_rgba(0,0,0,0.05)]" data-testid="unlocks-card">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Unlocked for you from the live class</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((it) => { const I = ICON[it.kind]; return (
          <button key={it.kind + it.label} type="button" onClick={() => navigate(it.to)} className="inline-flex items-center gap-2 rounded-full bg-white border border-amber-200 px-3.5 py-2 text-sm font-semibold text-slate-800 hover:border-amber-400 hover:shadow transition" data-testid={`unlock-${it.kind}`}>
            <I size={14} className="text-amber-600" /> {it.label} <ArrowRight size={13} className="text-slate-400" />
          </button>); })}
      </div>
    </div>
  );
}
